import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

/**
 * Modal to connect / re-key a social account: collects an account name and a
 * raw token, then hands them to `onSave`. The token is **write-only** — it is
 * sent once and never read back (the API only ever returns a last-4 hint).
 * `onSave` may throw to keep the dialog open and surface its error inline.
 */
export function ConnectPlatformDialog({
  isOpen,
  onClose,
  platformLabel,
  initialAccountName,
  hasExistingCredential,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  platformLabel: string;
  initialAccountName?: string;
  hasExistingCredential?: boolean;
  onSave: (accountName: string, token: string) => Promise<void>;
}): JSX.Element {
  const [accountName, setAccountName] = useState(initialAccountName ?? "");
  const [token, setToken] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever the dialog (re)opens for a platform.
  useEffect(() => {
    if (isOpen) {
      setAccountName(initialAccountName ?? "");
      setToken("");
      setError(null);
    }
  }, [isOpen, initialAccountName]);

  const canSave = accountName.trim().length > 0 && token.trim().length > 0 && !working;

  const handleSave = async () => {
    setWorking(true);
    setError(null);
    try {
      await onSave(accountName.trim(), token.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => !working && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { maxWidth: 460 } }}
    >
      <DialogTitle>Connect {platformLabel}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          {hasExistingCredential
            ? "Replace the stored access token for this account."
            : "Paste the platform access token. It's encrypted at rest and never shown again — only a last-4 hint."}
        </DialogContentText>

        <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
          <TextField
            id="cpd-account"
            label="Account name"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            placeholder="e.g. XOP Networks"
            autoComplete="off"
            fullWidth
          />
          <TextField
            id="cpd-token"
            label="Access token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste the token…"
            multiline
            rows={3}
            autoComplete="off"
            spellCheck={false}
            fullWidth
            inputProps={{ sx: { fontFamily: "ui-monospace, monospace", fontSize: 12 } }}
          />
          {error && (
            <Typography sx={{ fontSize: 14, color: "error.main" }} data-testid="connect-error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={working}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {working ? "Saving…" : "Save & connect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
